import React, { useState, useEffect } from 'react';
import { When } from 'react-if';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import {
    Box,
    Card, Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
    Typography
} from '@mui/material';
import Loading from '../../loading';
import { RootState } from '../../../store';
import { PaginationFactory, PaginatorFactory } from '../../../factory';
import { AlertService } from '../../../services/alert.service';
import { AlertTypes } from '../../../types';
import { downloadCsv } from '../../../hooks/useCsvDownload';
import NoData from '../../no-data';
import {RISK_REVIEW_COLUMNS} from "../../../builders/risk-reviews-builders";
import {Download} from "@mui/icons-material";
import {Team} from "../../../types/teams.types";
import {RiskReviewDetailsDrawer} from "../../details-drawers/risk-review-details-drawer";


interface Props {
    fromDate: string;
    toDate: string;
    selectedTeams: Team[];
}

export const RiskReviewsList: React.FC<Props> = ({fromDate,toDate,selectedTeams}) => {
    const RISK_REVIEW_ALERT_TYPE = 'RiskReview';
    const { pagination: { total, items }, loading } = useSelector((state: RootState) => state.alert);
    const [detailsModal, setDetailsModal] = useState(false);
    const [selectedAlert, setSelectedAlert] = useState<AlertTypes.Model>({} as AlertTypes.Model);
    const teamIds = selectedTeams.map(team => (team as any)?.id);
    const [pagination, setPagination] = useState<PaginatorFactory<any>>({
      page: 0,
      perPage: 25,
    });

    const makeCsvDownload = async () => {
        const res: PaginationFactory<AlertTypes.Model> = await AlertService.fetchMany(
            { perPage: 100000, page: pagination.page + 1, filter: {
                    initiatedBy:'',
                    initiatedAt:{
                        from:fromDate,
                        to:toDate,
                    },
                    teamIds: teamIds,
                    alertType: RISK_REVIEW_ALERT_TYPE
                }},
            undefined,
            true,
            false,
        ) as PaginationFactory<AlertTypes.Model>;

        const csvArray = res.items?.map((alert) => ({
            'Reported By': `${alert?.user?.firstName ?? ''} ${alert?.user?.lastName ?? ''}`,
            'Team Name': getTeamsOfUser(alert?.userId),
            Address: alert?.address ?? '',
            Status: alert?.status === "pending-review" ? "Pending Review" : "Reviewed",
            'Date Created': (dayjs(alert?.initiatedAt).format('MM/DD/YYYY hh:mm A')) ?? ''
        }));

        downloadCsv(csvArray, 'pom-risk-reviews');
    }

    const getTeamsOfUser = (userId: string) => {
        let usersTeam = selectedTeams.filter(team => {
            return team?.membersIds?.some(mid => mid === userId);
        }).map(team => team.name);
        if(usersTeam.length > 0) {
            return usersTeam.join(', ');
        } else {
            return 'N/A';
        }
    }

    useEffect(() => {
        AlertService.fetchMany({
            ...pagination,
            page: pagination.page + 1,
            filter: {
                initiatedBy:'',
                fromDate : fromDate,
                toDate : toDate,
                teamIds: teamIds,
                alertType: RISK_REVIEW_ALERT_TYPE
            }
        });
    }, [pagination?.perPage, pagination?.page, fromDate, toDate, selectedTeams]);

    return (
        <>
            <Card sx={{ mt: 2 }}>
                <Box
                    sx={{
                        borderRadius: 0,
                        padding : 2
                    }}
                >
                    <Grid container mr={2} display={'flex'} justifyContent={'space-between'}>
                        <Typography fontSize={16} fontWeight={500}>
                            Reports
                        </Typography>
                        <Tooltip sx={{cursor:'pointer'}} title={"Download Report List"}>
                            <Download onClick={async () => {
                                await makeCsvDownload();
                            }} />
                        </Tooltip>
                    </Grid>
                </Box>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow> 
                                {RISK_REVIEW_COLUMNS?.map((column) => (
                                    <TableCell key={column.headerName}>
                                        <Typography  variant="body2" sx={{ color: 'text.primary' }}>
                                            {column?.headerName}
                                        </Typography>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <When condition={!loading?.fetchMany}>
                            <TableBody>
                                {items?.map((alert) => (
                                    <TableRow 
                                        key={alert?.id}
                                        sx={{ 
                                            ":hover": { 
                                                backgroundColor: '#F0F0F0',
                                                cursor: 'pointer'
                                            }}
                                        }
                                        onClick={() => {
                                            setSelectedAlert(alert);
                                            setDetailsModal(true);
                                        }}
                                    >
                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                {alert?.user?.firstName} {alert?.user?.lastName}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                {getTeamsOfUser(alert?.userId)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                {alert?.address ? alert?.address : 'N/A'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                {alert?.status === "pending-review" ? "Pending Review" : "Reviewed"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                {dayjs(alert?.initiatedAt).format('MM/DD/YYYY hh:mm A')}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </When>
                    </Table>
                    <When
                        condition={
                            loading?.fetchMany === false && (!items || items?.length < 1)
                        }
                    >
                        <NoData
                            buttonText={null}
                            onClick={() => {}}
                            title={'No risk alerts'}
                            description={
                                'There are no risk alerts created.'
                            }
                        />
                    </When>
                    <When condition={loading?.fetchMany}>
                        <Loading />
                    </When>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[25, 50, 100]}
                    component="div"
                    count={total ?? 0}
                    rowsPerPage={pagination?.perPage}
                    page={pagination?.page}
                    onPageChange={(e, p) => setPagination(pag => ({ ...pag, page: p }))}
                    onRowsPerPageChange={(e) => setPagination(pag => ({ ...pag, perPage: parseInt(e.target.value, 10) }))}
                />
            </Card>
            {detailsModal && selectedAlert &&
            <RiskReviewDetailsDrawer
                type={selectedAlert?.alertType!}
                alert={selectedAlert}
                visible={detailsModal}
                resolveLoading={loading?.resolve}
                onClose={async (reload) => {
                    setDetailsModal(false);
                    setSelectedAlert({} as AlertTypes.Model);
                    if(reload)
                        await AlertService.fetchMany({ ...pagination, page: pagination.page + 1, filter: {
                                initiatedBy:'',
                                fromDate : fromDate,
                                toDate : toDate,
                                teamIds: teamIds,
                                alertType: RISK_REVIEW_ALERT_TYPE
                            } });
                }}
            />
            }
        </>
    )
}